// Orders sub-tabs: the prototype's four-tab row, mapped onto real order statuses.
//
// The prototype's tabs filter a demo `status` field with five made-up values
// (to_process / drx_created / ready_dispatch / completed / quotation). Those do not
// exist in the database. `enum_userOrders_status` has 36 values, of which eleven occur
// in tenant 1's live data:
//
//   DELIVERED 10543 · ORDER_ACTIVE 181 · SHIPPED_IN_TRANSIT 134 · CONSENT_PENDING 75
//   ORDER_PENDING 71 · CONSENT_GRANTED 50 · PAYMENT_CONFIRMED 22 · SHIPPED_ERROR 6
//   ORDER_CANCELLED 2 · OCS_PROCESSING 1 · ORDER_CONFIRMED 1
//
// The groups below cover the fulfilment stages the prototype's tabs mean, expressed in
// those real values. Statuses are listed even when currently rare or absent (e.g.
// SHIPPED_MEDICINES_PACKED) — they are live enum members that a status change can
// produce at any time, and a tab that silently omits one would hide orders.
//
// Every value here must exist in enum_userOrders_status, or Postgres rejects the whole
// query rather than ignoring the unknown one.

export const SUB_TABS = [
    {
        id: "to_process",
        label: "To Be Processed",
        // Confirmed but not yet through pre-sorting: awaiting consent, payment, or OCS.
        //
        // ORDER_CONFIRMED was omitted here for a long time: it is a live
        // enum_userOrders_status value, but it was missing from ALLOWED_status in
        // classes/UserOrder/Constants.js, and that guard rejects the whole query when
        // any one filter value is unlisted — so including it broke this tab entirely
        // rather than adding one row.
        //
        // Now fixed backend-side. The fix was not the one-liner it looked like:
        // ALLOWED_status was spread into FUTURE_ORDER_STATUSES, which drives the
        // NOT EXISTS subquery deciding reorder candidacy, so adding a status to the
        // filter allow-list silently changed the Reorders screen too. The two lists are
        // now separate, and reorder eligibility was verified unchanged.
        statuses: [
            "ORDER_PENDING",
            "ORDER_ACTIVE",
            "ORDER_CONFIRMED",
            "CONSENT_PENDING",
            "CONSENT_GRANTED",
            "PAYMENT_CONFIRMED",
            // Follows PAYMENT_CONFIRMED directly when the whole order is in stock.
            // Requires pillup-api with the ORDER_PROCURED migration -- an unknown
            // value rejects this whole tab's query.
            "ORDER_PROCURED",
            "PAYMENT_SUCCESS",
            "OCS_PENDING",
            "OCS_PROCESSING",
        ],
    },
    {
        id: "drx_created",
        label: "DRX Created",
        // The digital-Rx confirmation loop with the patient.
        statuses: ["USER_DRX_PENDING", "USER_DRX_ACCEPTED", "USER_DRX_REJECTED"],
    },
    {
        id: "ready_dispatch",
        label: "Ready to Dispatch",
        // Packed and onwards, up to but not including delivered.
        statuses: [
            "SHIPPED_PACKAGING",
            "SHIPPED_MEDICINES_PACKED",
            "SHIPPED_BOX_PACKED",
            "SHIPPED_READY_TO_SHIP",
            "MDM_QC",
            "SHIPPED_IN_TRANSIT",
        ],
    },
    {
        // No status filter at all — this is the tab the advanced filter panel appears on,
        // matching the prototype, where the seven extra controls are inert on the others.
        id: "all",
        label: "All Orders",
        statuses: [],
    },
];

export const DEFAULT_SUB_TAB = "to_process";

/** Row tinting, mirroring the prototype's four-swatch legend.
 *
 * `swatch` is a Tailwind class, not a colour value, so the legend has ONE source of
 * truth. The row tints themselves (see orderRowClass below) are near-white and
 * invisible in a 12px square, so each swatch is a deeper step of the same ramp
 * rather than the tint verbatim. */
export const LEGEND = [
    { key: "drx", label: "DRX Created (Pending Sorting)", swatch: "bg-amber-500" },
    { key: "ready", label: "Ready for Dispatch", swatch: "bg-elm-400" },
    { key: "today", label: "Order Received Today", swatch: "bg-amber-200" },
    { key: "done", label: "Completed / Archived", swatch: "bg-gray-300" },
];

const DRX_STATUSES = new Set(SUB_TABS[1].statuses);
const READY_STATUSES = new Set(SUB_TABS[2].statuses);
const DONE_STATUSES = new Set(["DELIVERED", "ORDER_DELIVERED", "ORDER_CANCELLED", "ORDER_INACTIVE"]);

function isToday(value) {
    if (!value) return false;
    const then = new Date(value);
    const now = new Date();
    return (
        then.getDate() === now.getDate() &&
        then.getMonth() === now.getMonth() &&
        then.getFullYear() === now.getFullYear()
    );
}

/**
 * Row tint class. Order matters — the prototype checks completed, then DRX, then ready,
 * then today, so a delivered order received today reads as completed rather than new.
 * Kept deliberately faint: this is a scanning aid layered under real content, not a
 * status badge, and it must not fight the status pill for attention.
 */
export function orderRowClass(row) {
    if (DONE_STATUSES.has(row.status)) return "!bg-[var(--ops-carbon-50)] text-[var(--ops-carbon-600)]";
    // amber-25 (#FFFBEE) is 17/255 off white in its darkest channel — on an actual
    // screen it reads as no tint at all, which is exactly what got flagged against the
    // prototype's solid-orange row. amber-100 (#FEF0C7) is the same ramp, just the step
    // that's actually visible at a glance rather than only on close inspection.
    if (DRX_STATUSES.has(row.status)) return "!bg-[var(--ops-amber-100)]";
    if (READY_STATUSES.has(row.status)) return "!bg-[var(--ops-evergreen-50)]";
    if (isToday(row.orderDate)) return "!bg-[var(--ops-amber-100)]";
    return "";
}
